import { useState, useEffect } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Field } from "./theme";
import { GET, POST } from "./api";
import { useLang } from "./i18n";

const AUTO_RAM = 64;

export default function UserRequests({ me, vms, requests, config, images, fetchAll }) {
  const { t } = useLang();
  const [tab, setTab] = useState("new_vm");
  const [submitting, setSubmitting] = useState(false);
  const [gpuAvail, setGpuAvail] = useState({});

  const serverOpts = config && config.servers
    ? Object.entries(config.servers).map(([k, v]) => ({ v: k, l: v.label || k }))
    : [];
  const imageOpts = (images || []).filter(i => i.available).map(i => ({ v: i.id, l: i.name }));

  const [newVm, setNewVm] = useState({
    server: serverOpts.length > 0 ? serverOpts[0].v : "",
    image: imageOpts.length > 0 ? imageOpts[0].v : "ubuntu-base",
    with_gpu: false, gpu_count: "1",
    cpu: "4", ram: "8", disk: "100", reason: "",
  });

  // GPU 가용 현황 로드
  useEffect(() => {
    const fetchGpu = async () => {
      for (const [sid] of Object.entries(config?.servers || {})) {
        try {
          const data = await GET(`/api/gpu-available/${sid}`);
          setGpuAvail(prev => ({ ...prev, [sid]: data }));
        } catch (e) {}
      }
    };
    fetchGpu();
    const id = setInterval(fetchGpu, 10000);
    return () => clearInterval(id);
  }, [config]);

  const vmOpts = (vms || []).map(vm => ({ v: vm.id, l: `${vm.id} (${vm.server})` }));
  const [resForm, setResForm] = useState({ vm_id: vmOpts.length > 0 ? vmOpts[0].v : "", cpu: "0", ram: "0", reason: "" });

  const selectedFree = (gpuAvail[newVm.server] || []).filter(g => g.available).length;
  const gpuEnough = !newVm.with_gpu || selectedFree >= parseInt(newVm.gpu_count || "0", 10);
  const ramOk = parseInt(newVm.ram, 10) <= AUTO_RAM;
  const willAutoApprove = gpuEnough && ramOk;

  const submitNewVm = async () => {
    if (!newVm.server || !newVm.reason) return alert("서버와 사유를 입력하세요");
    setSubmitting(true);
    try {
      const res = await POST("/api/requests", {
        type: "new_vm",
        server: newVm.server,
        image: newVm.image,
        with_gpu: newVm.with_gpu,
        gpu_count: newVm.with_gpu ? parseInt(newVm.gpu_count, 10) : 0,
        cpu: parseInt(newVm.cpu, 10),
        ram: parseInt(newVm.ram, 10),
        disk: parseInt(newVm.disk, 10),
        reason: newVm.reason,
      });
      await fetchAll();
      if (res.auto_approved) {
        alert(`VM 생성 완료: ${res.vm_id} (SSH: ${res.ssh_port})`);
      } else {
        alert(res.message || "관리자 승인 대기 중입니다");
      }
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const submitResource = async () => {
    const cpu = parseInt(resForm.cpu, 10), ram = parseInt(resForm.ram, 10);
    if (cpu === 0 && ram === 0) return alert("변경할 자원을 입력하세요");
    if (!resForm.reason) return alert("사유를 입력하세요");
    setSubmitting(true);
    try {
      const res = await POST("/api/requests", {
        type: "resource", vm_id: resForm.vm_id, cpu, ram, reason: resForm.reason,
      });
      await fetchAll();
      alert(res.auto_approved ? "자동 승인됨" : "관리자 승인 대기 중");
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const getStatusLabel = (r) => r.display_status || r.status;
  const STATUS_COLOR_MAP = { "대기중": C.amber, "거절": C.red, "생성실패": C.red, "생성완료": C.green, "반납완료": C.dim2, "승인": C.green };
  const getStatusColor = (r) => STATUS_COLOR_MAP[getStatusLabel(r)] || C.green;

  const fmtDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d + "Z");
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")} ${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <Btn small variant={tab === "new_vm" ? "cyan" : "ghost"} onClick={() => setTab("new_vm")}>{t("new_vm_request")}</Btn>
        <Btn small variant={tab === "resource" ? "cyan" : "ghost"} onClick={() => setTab("resource")}>{t("resource_change")}</Btn>
      </div>

      {tab === "new_vm" && (
        <Card>
          <CardHead>
            <span style={{ fontSize: 12, fontFamily: "monospace", color: C.text, fontWeight: 700 }}>{t("new_vm_request")}</span>
            <Badge label={willAutoApprove ? t("auto_create") : t("admin_approval")}
              color={willAutoApprove ? C.green : C.amber} />
          </CardHead>
          <div style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={t("server")} value={newVm.server} onChange={v => setNewVm(p => ({ ...p, server: v }))}
                options={serverOpts} />
              <Field label={t("image")} value={newVm.image} onChange={v => setNewVm(p => ({ ...p, image: v }))}
                options={imageOpts.length > 0 ? imageOpts : [{v:"ubuntu-base",l:"Ubuntu 22.04"}]} />
            </div>

            {/* GPU 선택 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                fontSize: 12, color: C.text, marginBottom: 8 }}>
                <input type="checkbox" checked={newVm.with_gpu}
                  onChange={e => {
                    const gpu = e.target.checked;
                    setNewVm(p => ({ ...p, with_gpu: gpu, ram: gpu && parseInt(p.ram) < 16 ? "16" : p.ram }));
                  }} />
                {t("use_gpu")}
              </label>
              {newVm.with_gpu && (
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12 }}>
                  <div style={{ fontSize: 10, color: C.dim2, marginBottom: 8, fontFamily: "monospace" }}>
                    {newVm.server}: {t("gpu_available")} {selectedFree}
                    {(gpuAvail[newVm.server] || []).map(g => (
                      <span key={g.gpu_id} style={{ marginLeft: 8,
                        color: g.available ? C.green : C.red }}>
                        GPU{g.gpu_id}:{g.available ? t("free") : g.used_by?.user_id}
                      </span>
                    ))}
                  </div>
                  <Field label={t("gpu_count")} value={newVm.gpu_count}
                    onChange={v => setNewVm(p => ({ ...p, gpu_count: v }))}
                    options={[1,2,3,4].map(n => ({v:String(n), l:`${n}개`}))} />
                  {!gpuEnough && (
                    <div style={{ fontSize: 10, color: C.amber, fontFamily: "monospace" }}>
                      {t("gpu_shortage")}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label={t("cpu_cores")} value={newVm.cpu} onChange={v => setNewVm(p => ({ ...p, cpu: v }))} type="number" />
              <Field label={t("ram_gb")} value={newVm.ram} onChange={v => setNewVm(p => ({ ...p, ram: v }))} type="number" />
              <Field label={t("disk_gb")} value={newVm.disk} onChange={v => setNewVm(p => ({ ...p, disk: v }))} type="number" />
            </div>
            {parseInt(newVm.ram, 10) > AUTO_RAM && (
              <div style={{ fontSize: 10, color: C.amber, fontFamily: "monospace", marginBottom: 8 }}>
                RAM {AUTO_RAM}{t("ram_over_limit")}
              </div>
            )}

            <Field label={t("reason")} value={newVm.reason} onChange={v => setNewVm(p => ({ ...p, reason: v }))}
              placeholder={t("vm_purpose")} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <Btn variant="cyan" disabled={submitting} onClick={submitNewVm}>
                {submitting ? t("submitting") : willAutoApprove ? t("create_vm") : t("submit")}
              </Btn>
            </div>
          </div>
        </Card>
      )}

      {tab === "resource" && (
        <Card>
          <CardHead>
            <span style={{ fontSize: 12, fontFamily: "monospace", color: C.text, fontWeight: 700 }}>{t("resource_change")}</span>
          </CardHead>
          <div style={{ padding: 16 }}>
            {vmOpts.length === 0 ? (
              <div style={{ fontSize: 12, color: C.dim2, textAlign: "center", padding: 20 }}>{t("no_vm_to_change")}</div>
            ) : (
              <>
                <Field label={t("vm")} value={resForm.vm_id} onChange={v => setResForm(p => ({ ...p, vm_id: v }))}
                  options={vmOpts} />
                <div style={{ fontSize: 9, color: C.dim2, fontFamily: "monospace", marginBottom: 12 }}>
                  RAM {AUTO_RAM}G 이하 자동 승인 / 초과 시 관리자 승인
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Field label={t("cpu_delta")} value={resForm.cpu} onChange={v => setResForm(p => ({ ...p, cpu: v }))} type="number" />
                  <Field label={t("ram_delta")} value={resForm.ram} onChange={v => setResForm(p => ({ ...p, ram: v }))} type="number" />
                </div>
                <Field label={t("reason")} value={resForm.reason} onChange={v => setResForm(p => ({ ...p, reason: v }))} placeholder={t("change_reason")} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                  <Btn variant="cyan" disabled={submitting} onClick={submitResource}>
                    {submitting ? t("submitting") : t("submit")}
                  </Btn>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* History */}
      <SectionTitle title={t("request_history")} />
      {(!requests || requests.length === 0) ? (
        <div style={{ fontSize: 12, color: C.dim2, textAlign: "center", padding: 20 }}>{t("no_history")}</div>
      ) : (
        <Card style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {[t("role"), t("vm"), "-", t("reason"), t("status"), t("created_at")].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 9, color: C.dim2, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...(requests || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(r => {
                const typeLabel = { new_vm: "새 VM", resource: "자원 변경", gpu: "GPU" }[r.type] || r.type;
                let detail = "";
                if (r.type === "new_vm") detail = `${r.cpu}C ${r.ram}G ${r.disk}D${r.gpu_count > 0 ? ` GPU${r.gpu_count}` : ""}`;
                else if (r.type === "resource") detail = `CPU:${r.cpu} RAM:${r.ram}`;
                else if (r.type === "gpu") detail = `GPU x${r.gpu_count}`;
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}08` }}>
                    <td style={{ padding: "6px 10px" }}><Badge label={typeLabel} color={C.blue} /></td>
                    <td style={{ padding: "6px 10px", color: C.text }}>{r.vm_id || r.server || "-"}</td>
                    <td style={{ padding: "6px 10px", color: C.dim2 }}>{detail}</td>
                    <td style={{ padding: "6px 10px", color: C.dim2, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.reason || "-"}</td>
                    <td style={{ padding: "6px 10px" }}><Badge label={getStatusLabel(r)} color={getStatusColor(r)} /></td>
                    <td style={{ padding: "6px 10px", color: C.dim2, whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
